/**
 * FoldableList Class
 *
 * A dynamic participant list component with collapsible functionality using jQuery.
 *
 * Usage:
 * const participantsConfig = [
 *     { name: 'John Doe', lang: 'en-john', icons: [{ src: 'icon1.png', alt: 'Icon 1' }] },
 *     { name: 'Jane Smith', lang: 'en-jane', icons: [] }
 * ];
 *
 * const participantsList = new ParticipantsList('.scene-container', participantsConfig, (index, participant) => {
 *     console.log('FoldableList clicked:', participant);
 * });
 *
 * Parameters:
 * - containerSelector: String - Selector for the main container.
 * - participantsConfig: Array - List of participant configurations containing name, lang, and icons.
 * - onParticipantClick: Function - Callback triggered when a participant is clicked.
 */

import { ICON } from "../Icons/Icons";
import { createPopper } from "@popperjs/core";
import preventOverflowPopperPlugin from "@popperjs/core/lib/modifiers/preventOverflow.js";
import flipPopperPlugin from "@popperjs/core/lib/modifiers/flip.js";
import UserActivity from "../Peoples/UserActivity";

let activeTourParticipantDrag = null;
let documentTourDragBound = false;
let suppressNextClick = false;

function clearTourParticipantDrag() {
  activeTourParticipantDrag = null;
  $(".list-container.is-drop-target").removeClass("is-drop-target");
  $(".participant-row.is-dragging").removeClass("is-dragging");
}

export default class FoldableList {
  constructor({
    model,
    containerSelector,
    participantsConfig,
    onParticipantClick,
    dataLang,
    title,
    hideHeader = false,
    onIconClick,
    enableSearch = false,
    onSearch,
    dragListId = null,
    onParticipantDrop = null,
    canDragParticipant = null,
  }) {
    this.model = model;
    this.container = $(containerSelector);
    this.participantsConfig = participantsConfig;
    this.onParticipantClick = onParticipantClick;
    this.onIconClick = onIconClick;
    this.participantsList = $('<div class="participants-list"></div>');
    this.isFolded = true;
    this.dataLang = dataLang;
    this.title = title;
    this.hideHeader = hideHeader;
    this.itemIds = new Set();
    this.threeDotsMenuContainerOpened = false;
    this.isMobile = false;
    this.enableSearch = enableSearch;
    this.onSearch = onSearch;
    this.dragListId = dragListId;
    this.onParticipantDrop = onParticipantDrop;
    this.canDragParticipant = canDragParticipant;
    this.userActivityInstances = new Map(); // Store UserActivity instances

    this.init();
  }

  init() {
    this.container.empty(); // Clear any existing content in the container
    this.setupSearch();
    this.setupHeader();
    this.renderParticipants();
    this.setupToggle();
    this.setupOptions();
    this.setupParticipantDrag();
    this.initialised = true;
    this.initListeners();
  }

  setupSearch() {
    if (!this.enableSearch) {
      return;
    }
    const form = $(`
      <div class="search-container">
        <input type="text" name="search" placeholder="Search for ${this.title}" />
        ${ICON.SEARCH}
      </div>
    `);
    // Append the search to the container
    this.container.append(form);

    let searchTimeout = null;
    this.container.find('[name="search"]').on("input", (e) => {
      const search = e.target.value;
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.onSearch && this.onSearch(search);
      }, 300);
    });
  }

  setupHeader() {
    if (this.hideHeader) {
      return;
    }

    // Add a header with icons-wrapper
    const header = $(`
      <div class="participant-header">
        <div class="scene-title" data-lang="${this.dataLang}">${this.title} <span class="item-count">(0)</span></div>
        <div class="icons-wrapper">
          <div class="icon-wrapper icon-wrapper--no-hover icon-arrow-down-wrapper">${ICON.ARROW_DOWN}</div>
          <div class="icon-wrapper icon-wrapper--no-hover icon-arrow-up-wrapper">${ICON.ARROW_UP}</div>
        </div>
      </div>
    `);

    // Append the header to the container
    this.container.append(header);
    this.updateToggleIcons();
  }

  renderParticipants() {
    if (!this.initialised) {
      this.container.append(this.participantsList);
    }
    // Render participants from the configuration
    this.participantsConfig.forEach((participant, index) => {
      this.renderSingleItem(participant);
    });

    this.container.find('.item-count').text(`(${this.itemIds.size})`);
  }

  setupToggle() {
    const arrowDown = this.container.find('.icon-arrow-down-wrapper');
    const arrowUp = this.container.find('.icon-arrow-up-wrapper');
    $(arrowDown).hide();

    // Add toggle functionality
    arrowDown.on('click', (event) => {
      event.stopPropagation();
      this.isFolded = false;
      this.container.addClass('folded');
      this.participantsList.toggle(!this.isFolded);
      $(arrowDown).hide();
      $(arrowUp).show();
    });
    arrowUp.on('click', (event) => {
      event.stopPropagation();
      this.isFolded = true;
      this.container.removeClass('folded');
      this.participantsList.toggle(!this.isFolded);
      $(arrowDown).show();
      $(arrowUp).hide();
    });
  }

  removeItem(id) {
    const itemToRemove = this.participantsList.find(`[data-participant-id="${id}"]`);
    if (itemToRemove.length > 0) {
      // Clean up event handlers
      itemToRemove.find('*').off();
      itemToRemove.off();

      // Clean up any popper instances
      const menuContainer = itemToRemove.find('.menu-container');
      if (menuContainer.length) {
        const popper = menuContainer.data('popper');
        popper?.destroy();
        menuContainer.data('popper', null);
      }

      // Remove the element
      itemToRemove.remove();

      // Update internal state
      this.itemIds.delete(id);
      this.participantsConfig = this.participantsConfig.filter(item => item.id !== id);

      // Clean up UserActivity instance if exists
      const userActivityInstance = this.userActivityInstances.get(id);
      if (userActivityInstance) {
        // Add cleanup method if UserActivity has one
        if (typeof userActivityInstance.destroy === 'function') {
          userActivityInstance.destroy();
        }
        this.userActivityInstances.delete(id);
      }

      // Update item count
      this.container.find('.item-count').text(`(${this.itemIds.size})`);
    }
  }

  renderSingleItem(participant) {
    const threeDotsContainer = participant.threeDotsContainer || '';
    let userActivityContainer = '';
    let userActivityInstance = null;
    let participantAvatarMood = '';

    // Initialize UserActivity if needed
    if (participant.userActivity) {
      userActivityContainer = '<div class="user-activity-container"></div>';
    }

    if (participant.showCurrentMood) {
      participantAvatarMood += `<div class="participant-avatar-mood mood-${participant.currentUserMood}"></div>`;
    }

    const row = $(`
        <div class="participant-row" data-participant-id="${participant.id}">
          <div class="participant-info">
            <div class="participant-avatar">
              <div class="participant-avatar-star">
                ${ICON.SMALL_STAR}
              </div>
              <div class="participant-avatar-image">
                <img src="${participant.userAvatarUrl}" onerror="this.src='/textures/defaultAvatar.png'" alt="${participant.name || ''}">
              </div>
              ${participantAvatarMood}
            </div>
            <div class="participant-name single-name">${participant.name || ''}</div>
            <div class="participant-name-and-scene-holder">
              <div class="participant-name">${participant.name || ''}</div>
              <div class="participant-scene-name">${participant.currentSceneName || ''}</div>
            </div>
          </div>
          
          <div class="icons-wrapper">
            ${userActivityContainer}
            ${(participant.icons || []).map(icon => `
              <div class="icon-wrapper icon-arrow-down-wrapper${String(icon.src || "").includes("icon-message") ? " icon-wrapper--message" : ""}">${icon.src || ''}</div>
            `).join('')}
            ${threeDotsContainer}
          </div>
        </div>
      `);

    // Initialize UserActivity after row is created
    if (participant.userActivity) {
      const container = row.find('.user-activity-container');
      userActivityInstance = new UserActivity({
        activeState: 'none',
        container: container,
        onIconClick: ({ state, event }) => {
          participant.onActivityClick && participant.onActivityClick(state, event, participant);
        }
      });
      // Store the instance for cleanup
      this.userActivityInstances.set(participant.id, userActivityInstance);
    }
    row.find(".participant-avatar").on("click", (e) => {
      participant.threeDotsCallback && participant.threeDotsCallback(0, participant, e, this?.userActivityInstances.get(participant.id));
    });

    if (this.dragListId && (!this.canDragParticipant || this.canDragParticipant(participant))) {
      this.attachDragHandle(row, participant);
    }

    if (participant.customRender) {
      participant.customRender(row);
    }

    if (participant.threeDotsContainer) {
      row.find('.menu-item').each((index, element) => {
        $(element).on('click', (event) => {
          event.stopPropagation();
          participant.threeDotsCallback && participant.threeDotsCallback(index, participant, event, this?.userActivityInstances.get(participant.id));
          const menu = $(row).find(".menu-container");
          if (menu.length) {
            menu.hide();
            const popper = menu.data('popper');
            popper?.destroy();
            menu.data('popper', null);
          }
        });
      });

      // Initialize menu container
      const menuContainer = row.find('.menu-container');
      if (menuContainer.length) {
        menuContainer.hide();
        const popper = menuContainer.data('popper');
        popper?.destroy();
        menuContainer.data('popper', null);
      }
    }
    // Add click event for each participant
    row.on('click', (e) => {
      if (suppressNextClick || row.hasClass("is-invitation-locked")) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.onParticipantClick && this.onParticipantClick(participant, e);
    });

    // Add click events for icons
    row.find('.icon-wrapper').each((index, iconElement) => {
      $(iconElement).on('click', (event) => {
        event.stopPropagation();

        this.onIconClick && this.onIconClick(index, participant, event);

        const iconWrapper = $(event.target).closest('.icon-wrapper');
        const iconSvg = iconWrapper.find('svg');

        if (iconSvg.hasClass("icon-three-dots")) {
          // Close all other menus first
          this.closeAllExtraMenus();

          const menu = iconWrapper.parent().find(".menu-container");
          if (!menu.length) return;

          let popper = menu.data('popper');
          if (menu.is(":visible")) {
            menu.hide();
            popper?.destroy();
            menu.data('popper', null);
          } else {
            popper = createPopper(iconWrapper[0], menu[0], {
              placement: 'bottom-end',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0]
                  }
                },
                preventOverflowPopperPlugin,
                flipPopperPlugin,
              ]
            });
            menu.data('popper', popper).show();
          }
        }
      });
    });
    this.itemIds.add(participant.id);
    this.participantsList.append(row);
  }

  renderGhostItem({ id, name, userAvatarUrl }) {
    const existing = this.participantsList.children(`[data-participant-id="${id}"]`);
    if (existing.length) {
      if (existing.hasClass("participant-row--ghost")) {
        existing.find(".participant-name").text(name);
        existing.find("img").attr("src", userAvatarUrl || "/textures/defaultAvatar.png");
      }
      return;
    }
    const row = $(`
      <div class="participant-row participant-row--ghost" data-participant-id="${id}">
        <div class="participant-info">
          <div class="participant-avatar">
            <div class="participant-avatar-image">
              <img alt="" draggable="false" src="/textures/defaultAvatar.png">
            </div>
          </div>
          <div class="participant-name single-name"></div>
        </div>
      </div>
    `);
    row.find(".participant-name").text(name);
    row.find("img").attr("src", userAvatarUrl || "/textures/defaultAvatar.png");
    row.find("img").on("error", function onGhostAvatarError() {
      this.src = "/textures/defaultAvatar.png";
    });
    this.itemIds.add(id);
    this.participantsList.append(row);
    this.container.find(".item-count").text(`(${this.itemIds.size})`);
  }

  ensureDragHandle(participant) {
    if (!this.dragListId || !participant) {
      return;
    }
    const row = this.participantsList.children(`[data-participant-id="${participant.id}"]`);
    if (!row.length) {
      return;
    }
    const allowed = !this.canDragParticipant || this.canDragParticipant(participant);
    const handle = row.find(".participant-row__drag");
    if (allowed && !handle.length) {
      this.attachDragHandle(row, participant);
    } else if (!allowed && handle.length) {
      handle.remove();
    }
  }

  attachDragHandle(row, participant) {
    const handle = $(`
      <span class="material-icons-outlined md-18 participant-row__drag" draggable="true">drag_indicator</span>
    `);
    row.find(".participant-info").prepend(handle);

    handle.on("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    handle.on("dragstart", (event) => {
      event.stopPropagation();
      const dataTransfer = event.originalEvent && event.originalEvent.dataTransfer;
      if (!dataTransfer) {
        event.preventDefault();
        return;
      }
      activeTourParticipantDrag = {
        participantId: participant.id,
        fromListId: this.dragListId,
      };
      dataTransfer.effectAllowed = "move";
      dataTransfer.setData("text/plain", String(participant.id));
      this.setRowDragImage(dataTransfer, row, event);
      row.addClass("is-dragging");
    });

    handle.on("dragend", () => {
      clearTourParticipantDrag();
    });
  }

  setRowDragImage(dataTransfer, row, event) {
    const source = row[0];
    if (!source || !dataTransfer.setDragImage) {
      return;
    }
    const rect = source.getBoundingClientRect();
    const clone = source.cloneNode(true);
    clone.classList.remove("is-dragging", "is-invitation-locked");
    clone.style.position = "fixed";
    clone.style.top = "-1000px";
    clone.style.left = "0";
    clone.style.width = `${rect.width}px`;
    clone.style.margin = "0";
    clone.style.opacity = "1";
    clone.style.pointerEvents = "none";
    document.body.appendChild(clone);
    const pointerX = Math.max(0, event.originalEvent.clientX - rect.left);
    const pointerY = Math.max(0, event.originalEvent.clientY - rect.top);
    dataTransfer.setDragImage(clone, pointerX, pointerY);
    setTimeout(() => clone.remove(), 0);
  }

  setupParticipantDrag() {
    if (!this.dragListId) {
      return;
    }

    if (!documentTourDragBound) {
      documentTourDragBound = true;
      // dragend does not fire if the drag handle is removed during drop
      // (the invite lock does that). Capture still covers a normal release.
      document.addEventListener("dragend", () => {
        clearTourParticipantDrag();
        // Releasing a drag fires a click on whatever is under the cursor.
        suppressNextClick = true;
      }, true);
      document.addEventListener("click", (event) => {
        if (!suppressNextClick) {
          return;
        }
        suppressNextClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      document.addEventListener("mousedown", () => {
        suppressNextClick = false;
      }, true);
    }

    this.container.on("dragover", (event) => {
      if (!activeTourParticipantDrag) {
        return;
      }
      event.preventDefault();
      const dataTransfer = event.originalEvent && event.originalEvent.dataTransfer;
      const sameList = activeTourParticipantDrag.fromListId === this.dragListId;
      if (dataTransfer) {
        dataTransfer.dropEffect = sameList ? "none" : "move";
      }
      this.container.toggleClass("is-drop-target", !sameList);
    });

    this.container.on("dragleave", (event) => {
      const next = event.relatedTarget;
      if (next && this.container[0].contains(next)) {
        return;
      }
      this.container.removeClass("is-drop-target");
    });

    this.container.on("drop", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.container.removeClass("is-drop-target");
      const drag = activeTourParticipantDrag;
      // Restore the source row before the drop handler removes its drag handle.
      clearTourParticipantDrag();
      if (!drag || drag.fromListId === this.dragListId) {
        return;
      }
      this.onParticipantDrop && this.onParticipantDrop({
        participantId: drag.participantId,
        fromListId: drag.fromListId,
        toListId: this.dragListId,
      });
    });
  }

  setupOptions() {
    const optionsIcon = this.container.find('.icon-three-dots');
    // Add click event for the options icon
    optionsIcon.on('click', (event) => {
      event.stopPropagation();
      // Add your custom options logic here
      console.log('Options icon clicked!');
    });
  }
  closeAllExtraMenus() {
    $(".menu-container").hide().each((index, element) => {
      $(element).data('popper')?.destroy();
      $(element).data('popper', null);
    })
  }

  initListeners() {
    this.documentClickHandler = (event) => {
      const target = $(event.target);
      const menu = target.closest(".menu-container");

      if (menu.length === 0) {
        // Hide all menus if clicked outside a menu
        this.closeAllExtraMenus();
      }
    };

    $(document).on("click", this.documentClickHandler);
  }

  updateToggleIcons() {
    this.container.find(".icon-arrow-down-wrapper").show().trigger("click");
  }

  disableGuestPermissions() {
    this.container.find(".menu-item[data-lang='play-chess-text']").addClass("disabled");
  }

  enableGuestPermissions() {
    this.container.find(".menu-item[data-lang='play-chess-text']").removeClass("disabled");
  }
  updateRowIcon(id) {
    const row = this.participantsList.find(`[data-participant-id="${id}"]`);
    if (row.length) {
      const icon = row.find(".participant-avatar-star");
      if (this.model.isFriend(id)) {
        icon.show();
      } else {
        icon.hide();
      }
    }
  }

  destroy() {
    // Clean up all UserActivity instances
    this.userActivityInstances.forEach((instance, id) => {
      if (typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    this.userActivityInstances.clear();

    // Clean up all popper instances
    this.participantsList.find('.menu-container').each((index, element) => {
      const popper = $(element).data('popper');
      popper?.destroy();
      $(element).data('popper', null);
    });

    // Remove all event handlers
    this.participantsList.find('*').off();
    this.participantsList.off();
    this.container.find('*').off();
    this.container.off();

    // Remove document listener
    $(document).off('click', this.documentClickHandler);

    // Clear the container
    this.container.empty();
  }
}
